import { action, makeObservable, observable, runInAction } from "mobx";
import { PermissionProject } from "../../../models/Employees/PermissionProject";
import {
  TeamPermissionActivityLog,
  TeamPermissionCatalog,
  TeamPermissionChange,
  TeamPermissionItem,
  TeamPermissionTeam,
} from "../../../models/Employees/TeamPermission";
import TeamPermissionAPI from "../api/TeamPermissionAPI";

const emptyCatalog = (project: PermissionProject): TeamPermissionCatalog => ({
  project,
  teams: [],
  permissions: [],
});

export class TeamPermissionStore {
  private api = new TeamPermissionAPI();

  catalog: TeamPermissionCatalog = emptyCatalog("swageazy");
  project: PermissionProject = "swageazy";
  selectedTeamIds: number[] = [];
  selectedPermissionIds: number[] = [];
  searchDraft = "";
  searchText = "";
  savedKeys: string[] = [];
  draftKeys: string[] = [];
  selectedRoleIds: number[] = [];
  loading = false;
  saving = false;
  error: string | null = null;
  saveError: string | null = null;
  savedMessage: string | null = null;
  activityLogs: TeamPermissionActivityLog[] = [];
  activityLoading = false;
  activityError: string | null = null;

  constructor() {
    makeObservable(this, {
      catalog: observable,
      project: observable,
      selectedTeamIds: observable,
      selectedPermissionIds: observable,
      searchDraft: observable,
      searchText: observable,
      savedKeys: observable,
      draftKeys: observable,
      selectedRoleIds: observable,
      loading: observable,
      saving: observable,
      error: observable,
      saveError: observable,
      savedMessage: observable,
      activityLogs: observable,
      activityLoading: observable,
      activityError: observable,
      clearSavedMessage: action,
      load: action,
      save: action,
      loadActivityLogs: action,
      toggle: action,
      setRoleSelected: action,
      setRolesSelected: action,
      setColumn: action,
      setRow: action,
      setVisible: action,
      reset: action,
      setProject: action,
      setSelectedTeamIds: action,
      setSelectedPermissionIds: action,
      setSearchDraft: action,
      setSearchText: action,
      clearFilters: action,
      hydrateFilters: action,
    });
  }

  load = async (project: PermissionProject = this.project) => {
    runInAction(() => {
      this.loading = true;
      this.error = null;
      this.saveError = null;
      this.savedMessage = null;
      this.catalog = emptyCatalog(project);
      this.savedKeys = [];
      this.draftKeys = [];
      this.selectedRoleIds = [];
    });
    try {
      const catalog = await this.api.fetchCatalog(project);
      runInAction(() => {
        this.applyCatalog(catalog);
      });
    } catch (error: unknown) {
      const message = readErrorMessage(error);
      runInAction(() => {
        this.error = message;
        this.catalog = emptyCatalog(project);
        this.savedKeys = [];
        this.draftKeys = [];
        this.selectedRoleIds = [];
      });
      console.error("TeamPermissionStore.load failed:", error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  save = async () => {
    const changes = this.changes();
    if (changes.length === 0 || this.saving) return;
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
      this.savedMessage = null;
    });
    try {
      const catalog = await this.api.saveChanges(this.project, changes);
      runInAction(() => {
        this.applyCatalog(catalog);
        this.savedMessage = "Team permissions saved.";
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.saveError = readErrorMessage(error);
      });
      console.error("TeamPermissionStore.save failed:", error);
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  };

  loadActivityLogs = async () => {
    runInAction(() => {
      this.activityLoading = true;
      this.activityError = null;
    });
    try {
      const page = await this.api.fetchActivityLogs(this.project);
      runInAction(() => {
        this.activityLogs = page.logs ?? [];
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.activityLogs = [];
        this.activityError = readErrorMessage(
          error,
          "Could not load team permission activity.",
        );
      });
      console.error("TeamPermissionStore.loadActivityLogs failed:", error);
    } finally {
      runInAction(() => {
        this.activityLoading = false;
      });
    }
  };

  isRoleSelected = (roleId: number): boolean =>
    this.selectedRoleIds.includes(roleId);

  setRoleSelected = (roleId: number, selected: boolean) => {
    this.setRolesSelected([roleId], selected);
  };

  setRolesSelected = (roleIds: number[], selected: boolean) => {
    const targetIds = roleIds.filter((roleId) => roleId > 0);
    if (targetIds.length === 0) return;
    if (selected) {
      const next = new Set(this.selectedRoleIds);
      targetIds.forEach((roleId) => next.add(roleId));
      this.selectedRoleIds = Array.from(next);
      return;
    }
    const drop = new Set(targetIds);
    this.selectedRoleIds = this.selectedRoleIds.filter((roleId) => !drop.has(roleId));
    targetIds.forEach((roleId) => this.revertRole(roleId));
  };

  toggle = (roleId: number, teamId: number) => {
    if (!this.isRoleSelected(roleId) || this.saving) return;
    const key = pairKey(roleId, teamId);
    if (this.draftKeys.includes(key)) {
      this.draftKeys = this.draftKeys.filter((item) => item !== key);
    } else {
      this.draftKeys = [...this.draftKeys, key];
    }
    this.savedMessage = null;
  };

  isIncluded = (roleId: number, teamId: number): boolean => {
    return this.draftKeys.includes(pairKey(roleId, teamId));
  };

  isSaved = (roleId: number, teamId: number): boolean => {
    return this.savedKeys.includes(pairKey(roleId, teamId));
  };

  columnState = (teamId: number, roleIds: number[]): "all" | "some" | "none" => {
    return pairState(roleIds, [teamId], this.isIncluded);
  };

  rowState = (roleId: number, teamIds: number[]): "all" | "some" | "none" => {
    return pairState([roleId], teamIds, this.isIncluded);
  };

  visibleState = (roleIds: number[], teamIds: number[]): "all" | "some" | "none" => {
    return pairState(roleIds, teamIds, this.isIncluded);
  };

  setColumn = (teamId: number, roleIds: number[], included: boolean) => {
    this.applyPairs(roleIds, [teamId], included);
  };

  setRow = (roleId: number, teamIds: number[], included: boolean) => {
    this.applyPairs([roleId], teamIds, included);
  };

  setVisible = (roleIds: number[], teamIds: number[], included: boolean) => {
    this.applyPairs(roleIds, teamIds, included);
  };

  private revertRole = (roleId: number) => {
    const prefix = `${roleId}:`;
    const savedForRole = this.savedKeys.filter((key) => key.startsWith(prefix));
    this.draftKeys = this.draftKeys
      .filter((key) => !key.startsWith(prefix))
      .concat(savedForRole);
  };

  private applyPairs = (roleIds: number[], teamIds: number[], included: boolean) => {
    const unlockedRoleIds = roleIds.filter((roleId) => this.isRoleSelected(roleId));
    const targets = new Set<string>();
    if (unlockedRoleIds.length === 0 || teamIds.length === 0) return;
    unlockedRoleIds.forEach((roleId) => {
      teamIds.forEach((teamId) => {
        targets.add(pairKey(roleId, teamId));
      });
    });
    const remaining = this.draftKeys.filter((key) => !targets.has(key));
    this.draftKeys = included
      ? remaining.concat(Array.from(targets))
      : remaining;
    this.savedMessage = null;
  };

  clearSavedMessage = () => {
    this.savedMessage = null;
  };

  reset = () => {
    this.draftKeys = this.savedKeys.slice();
    this.savedMessage = null;
    this.saveError = null;
  };

  hasUnsavedChanges = (): boolean => {
    if (this.draftKeys.length !== this.savedKeys.length) return true;
    const saved = new Set(this.savedKeys);
    return this.draftKeys.some((key) => !saved.has(key));
  };

  setProject = (project: PermissionProject) => {
    if (project === this.project && this.catalog.permissions.length > 0) return;
    if (this.hasUnsavedChanges()) {
      this.saveError =
        "Save or reset your changes before switching project.";
      return;
    }
    this.project = project;
    this.clearFilters();
    void this.load(project);
  };

  setSelectedTeamIds = (ids: number[]) => {
    this.selectedTeamIds = ids;
  };

  setSelectedPermissionIds = (ids: number[]) => {
    this.selectedPermissionIds = ids;
  };

  setSearchDraft = (value: string) => {
    this.searchDraft = value;
  };

  setSearchText = (value: string) => {
    this.searchText = value;
  };

  hydrateFilters = (query: {
    project: PermissionProject;
    search: string;
    teamIds: number[];
    permissionIds: number[];
  }) => {
    this.project = query.project;
    this.searchDraft = query.search;
    this.searchText = query.search;
    this.selectedTeamIds = query.teamIds;
    this.selectedPermissionIds = query.permissionIds;
  };

  clearFilters = () => {
    this.selectedTeamIds = [];
    this.selectedPermissionIds = [];
    this.searchDraft = "";
    this.searchText = "";
  };

  get teams(): TeamPermissionTeam[] {
    return this.catalog.teams;
  }

  get permissions(): TeamPermissionItem[] {
    return this.catalog.permissions;
  }

  visibleTeams(): TeamPermissionTeam[] {
    if (this.selectedTeamIds.length === 0) return this.teams;
    return this.teams.filter((team) => this.selectedTeamIds.includes(team.id));
  }

  visiblePermissions(): TeamPermissionItem[] {
    const query = this.searchText.trim().toLowerCase();
    const teamFilter = this.selectedTeamIds.length > 0;
    const permissionFilter = this.selectedPermissionIds.length > 0;
    const textFilter = query.length > 0;

    return this.permissions.filter((permission) => {
      if (
        permissionFilter &&
        !this.selectedPermissionIds.includes(permission.id)
      ) {
        return false;
      }
      if (textFilter) {
        const haystack = `${permission.name} ${permission.identifier}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      if (teamFilter && !permissionFilter && !textFilter) {
        return permission.teamIds.some((id) =>
          this.selectedTeamIds.includes(id),
        );
      }
      return true;
    });
  }

  private applyCatalog = (catalog: TeamPermissionCatalog) => {
    const visibleCatalog = omitAllPermission(catalog);
    const keys = keysFromCatalog(visibleCatalog);
    this.catalog = visibleCatalog;
    this.project = catalog.project;
    this.savedKeys = keys;
    this.draftKeys = [...keys];
    this.selectedRoleIds = [];
  };

  private changes = (): TeamPermissionChange[] => {
    const saved = new Set(this.savedKeys);
    const draft = new Set(this.draftKeys);
    const changes: TeamPermissionChange[] = [];
    draft.forEach((key) => {
      if (saved.has(key)) return;
      const pair = parsePairKey(key);
      changes.push({
        roleId: pair.roleId,
        teamId: pair.teamId,
        included: true,
      });
    });
    saved.forEach((key) => {
      if (draft.has(key)) return;
      const pair = parsePairKey(key);
      changes.push({
        roleId: pair.roleId,
        teamId: pair.teamId,
        included: false,
      });
    });
    return changes;
  };
}

function pairState(
  roleIds: number[],
  teamIds: number[],
  isIncluded: (roleId: number, teamId: number) => boolean,
): "all" | "some" | "none" {
  if (roleIds.length === 0 || teamIds.length === 0) return "none";
  let selected = 0;
  roleIds.forEach((roleId) => {
    teamIds.forEach((teamId) => {
      if (isIncluded(roleId, teamId)) selected += 1;
    });
  });
  if (selected === 0) return "none";
  if (selected === roleIds.length * teamIds.length) return "all";
  return "some";
}

function pairKey(roleId: number, teamId: number): string {
  return `${roleId}:${teamId}`;
}

function parsePairKey(key: string): { roleId: number; teamId: number } {
  const [roleId, teamId] = key.split(":");
  return { roleId: Number(roleId), teamId: Number(teamId) };
}

function omitAllPermission(catalog: TeamPermissionCatalog): TeamPermissionCatalog {
  const permissions = catalog.permissions.filter(
    (permission) => permission.identifier.trim().toLowerCase() !== "all",
  );
  return {
    ...catalog,
    permissions,
    teams: catalog.teams.map((team) => ({
      ...team,
      permissionCount: permissions.filter((permission) =>
        permission.teamIds.includes(team.id),
      ).length,
    })),
  };
}

function keysFromCatalog(catalog: TeamPermissionCatalog): string[] {
  const keys: string[] = [];
  catalog.permissions.forEach((permission) => {
    permission.teamIds.forEach((teamId) => {
      keys.push(pairKey(permission.id, teamId));
    });
  });
  return keys;
}

function readErrorMessage(
  error: unknown,
  fallback = "Could not load team permissions.",
): string {
  if (typeof error === "object" && error && "response" in error) {
    const response = (error as { response?: { data?: { message?: string } } })
      .response;
    if (response?.data?.message) return response.data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
